import { FC, ReactNode } from 'react';
import { Box, Button, CircularProgress, useTheme } from '@mui/material';
import { genericTexts } from '@utils/translations/en';
import FormViewHeader from './FormViewHeader';

interface FormPageLayoutProps {
  title: string | React.ReactNode;
  onBack: () => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  isSaveDisabled?: boolean;
  children: ReactNode;
}

/**
 * Shared layout for all full-page form screens (Invite User, Edit User, Edit Company, etc.).
 * Renders a back-arrow header, scrollable content area, and a fixed Save/Cancel action bar.
 */
const FormPageLayout: FC<FormPageLayoutProps> = ({
  title,
  onBack,
  onSave,
  onCancel,
  isSaving = false,
  isSaveDisabled = false,
  children,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        padding: '1.5rem',
        pb: '6rem',
        backgroundColor: '#FFFFFF',
        overflowY: 'auto',
      }}
    >
      <FormViewHeader title={title} onBack={onBack} />

      {children}

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '-24px',
          width: 'calc(100% + 24px)',
          height: 73,
          borderTop: `2px solid ${theme.palette.divider}`,
          px: 4,
          py: 2,
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: 2,
          background: theme.palette.common.white,
          zIndex: theme.zIndex.appBar,
        }}
      >
        <Button
          variant="contained"
          disableElevation
          onClick={onSave}
          disabled={isSaveDisabled}
          sx={{
            background: theme.palette.primary.main,
            borderRadius: '100px',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '0.875rem',
            px: '24px',
            py: '10px',
            color: '#F6F8F9',
            textTransform: 'none',
            minWidth: '5rem',
            '&:hover': { background: theme.palette.primary.main },
          }}
        >
          {isSaving ? <CircularProgress size={16} sx={{ color: '#F6F8F9' }} /> : genericTexts.Save}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disableElevation
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '0.875rem',
            px: '24px',
            py: '10px',
            border: '1px solid #2E4A4F',
            borderRadius: '100px',
            color: '#6C6C6C',
            textTransform: 'none',
            '&:hover': { borderColor: theme.palette.secondary.main },
          }}
        >
          {genericTexts.Cancel}
        </Button>
      </Box>
    </Box>
  );
};

export default FormPageLayout;
